import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
} from '@mui/material';
import {
  Edit as EditIcon,
  Block as DeactivateIcon,
  PlayArrow as ReactivateIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import BitemporalViewer from '../BitemporalViewer/BitemporalViewer';
import {
  deactivateEntity,
  reactivateEntity,
  getEntityHistory,
  getEntityAtTime,
  EntityType,
  BitemporalEntity,
} from '../../services/bitemporalApi';

interface EntityActionButtonsProps<T extends BitemporalEntity> {
  entity: T;
  entityType: EntityType;
  entityDisplayName: string;
  onEdit?: (entity: T) => void;
  onRefresh?: () => void;
  renderEntityInHistory?: (entity: T) => React.ReactNode;
  canEdit?: boolean;
  canDeactivate?: boolean;
  canReactivate?: boolean;
  canViewHistory?: boolean;
}

export default function EntityActionButtons<T extends BitemporalEntity>({
  entity,
  entityType,
  entityDisplayName,
  onEdit,
  onRefresh,
  renderEntityInHistory,
  canEdit = true,
  canDeactivate = true,
  canReactivate = true,
  canViewHistory = true,
}: EntityActionButtonsProps<T>) {
  const [loading, setLoading] = useState<'deactivate' | 'reactivate' | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    action: 'deactivate' | 'reactivate';
    entity: T | null;
  }>({ open: false, action: 'deactivate', entity: null });
  const [historyOpen, setHistoryOpen] = useState(false);

  // Use entity.is_active if available, otherwise assume active
  const isActive = entity.is_active !== false;
  const entityName = (entity as { name?: string }).name || entity.id;

  const handleDeactivate = async () => {
    if (!confirmDialog.entity) return;
    
    try {
      setLoading('deactivate');
      await deactivateEntity(entityType, confirmDialog.entity.id);
      
      // Close dialog and refresh data
      setConfirmDialog({ open: false, action: 'deactivate', entity: null });
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error(`Failed to deactivate ${entityDisplayName}:`, error);
      // TODO: Add proper error handling/notification
    } finally {
      setLoading(null);
    }
  };

  const handleReactivate = async () => {
    if (!confirmDialog.entity) return;
    
    try {
      setLoading('reactivate');
      await reactivateEntity(entityType, confirmDialog.entity.id);
      
      // Close dialog and refresh data
      setConfirmDialog({ open: false, action: 'reactivate', entity: null });
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error(`Failed to reactivate ${entityDisplayName}:`, error);
      // TODO: Add proper error handling/notification
    } finally {
      setLoading(null);
    }
  };

  const openConfirmDialog = (action: 'deactivate' | 'reactivate') => {
    setConfirmDialog({ open: true, action, entity });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, action: 'deactivate', entity: null });
  };

  const renderHistoryEntity = (entity: T): React.ReactNode => {
    if (renderEntityInHistory) {
      return renderEntityInHistory(entity);
    }
    
    // Default rendering for history
    return (
      <Box>
        <Typography variant="body1">
          <strong>ID:</strong> {entity.id}
        </Typography>
        {(entity as { name?: string }).name && (
          <Typography variant="body1">
            <strong>Name:</strong> {(entity as { name?: string }).name}
          </Typography>
        )}
        {entity.created_at && (
          <Typography variant="body1">
            <strong>Created:</strong> {new Date(entity.created_at).toLocaleString()}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <>
      <Box display="flex" gap={0.5}>
        {/* Edit Button */}
        {canEdit && onEdit && (
          <Tooltip title="Edit">
            <IconButton 
              size="small" 
              onClick={() => onEdit(entity)}
              color="primary"
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        )}

        {/* Deactivate/Reactivate Button */}
        {isActive && canDeactivate && (
          <Tooltip title="Deactivate">
            <IconButton 
              size="small" 
              onClick={() => openConfirmDialog('deactivate')}
              color="error"
              disabled={loading === 'deactivate'}
            >
              {loading === 'deactivate' ? (
                <CircularProgress size={20} />
              ) : (
                <DeactivateIcon />
              )}
            </IconButton>
          </Tooltip>
        )}

        {!isActive && canReactivate && (
          <Tooltip title="Reactivate">
            <IconButton 
              size="small" 
              onClick={() => openConfirmDialog('reactivate')}
              color="success"
              disabled={loading === 'reactivate'}
            >
              {loading === 'reactivate' ? (
                <CircularProgress size={20} />
              ) : (
                <ReactivateIcon />
              )}
            </IconButton>
          </Tooltip>
        )}

        {/* History Button */}
        {canViewHistory && (
          <Tooltip title="View History">
            <IconButton 
              size="small" 
              onClick={() => setHistoryOpen(true)}
              color="default"
            >
              <HistoryIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {confirmDialog.action === 'deactivate' ? 'Deactivate' : 'Reactivate'} {entityDisplayName}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {confirmDialog.action} "{entityName}"?
          </Typography>
          {confirmDialog.action === 'deactivate' && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This will make it unavailable but preserve it in the system with full audit history.
            </Typography>
          )}
          {confirmDialog.action === 'reactivate' && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This will create a new version and make it available again.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog}>
            Cancel
          </Button>
          <Button
            onClick={confirmDialog.action === 'deactivate' ? handleDeactivate : handleReactivate}
            color={confirmDialog.action === 'deactivate' ? 'error' : 'success'}
            variant="contained"
            disabled={loading !== null}
          >
            {loading !== null ? 'Processing...' : confirmDialog.action === 'deactivate' ? 'Deactivate' : 'Reactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* History Viewer */}
      {canViewHistory && (
        <BitemporalViewer
          entityId={entity.id}
          entityType={entityDisplayName}
          onGetHistory={(id) => getEntityHistory<T>(entityType, id)}
          onGetAtTime={(id, time) => getEntityAtTime<T>(entityType, id, time)}
          renderData={renderHistoryEntity}
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
        />
      )}
    </>
  );
}